import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Building2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { SupportChatThread, fmtTime } from "@/components/support/SupportChatThread";
import { STATUS } from "@/components/support/SupportWidget";

const FILTERS = [
    { v: "", l: "Tümü" },
    { v: "open", l: "Açık" },
    { v: "pending", l: "Bekliyor" },
    { v: "resolved", l: "Çözüldü" },
];

function TicketRow({ t, active, onClick }) {
    return (
        <button data-testid={`admin-ticket-${t.id}`} onClick={onClick} className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 transition-colors ${active ? "bg-sky-50 dark:bg-sky-950/30" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
            <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs font-bold text-slate-500 truncate flex-1">{t.hotel_name}</span>
                <span className="text-[10px] text-slate-400">{fmtTime(t.last_message_at)}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
                <span className={`text-sm truncate flex-1 ${t.unread ? "font-bold text-slate-900 dark:text-white" : "font-semibold text-slate-700 dark:text-slate-300"}`}>#{t.number} · {t.subject}</span>
                {t.unread > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{t.unread}</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS[t.status]?.c}`}>{STATUS[t.status]?.l}</span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{t.category_label}</span>
                <span className="text-[11px] text-slate-500 truncate flex-1">{t.last_message_preview}</span>
            </div>
        </button>
    );
}

function TicketHeader({ t, agents, onUpdate }) {
    return (
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-slate-900 dark:text-white">#{t.number} · {t.subject}</div>
                <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2">
                    <span>{t.hotel_name}</span>·<span>{t.created_by?.name} ({t.created_by?.email})</span>·<span>{t.category_label}</span>
                    {t.booking_id && <Link data-testid="admin-ticket-booking-link" to={`/admin/bookings?q=${encodeURIComponent(t.booking_code || t.booking_id)}`} className="inline-flex items-center gap-0.5 text-sky-600 font-semibold">Rez. {t.booking_code} <ExternalLink className="h-3 w-3" /></Link>}
                </div>
            </div>
            <select data-testid="admin-ticket-assign" value={t.assigned_to?.id || ""} onChange={(e) => onUpdate({ assigned_to_id: e.target.value || null })} className="px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-900 dark:text-white">
                <option value="">— Atanmadı —</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
            </select>
            <div className="flex gap-1">
                {Object.entries(STATUS).map(([k, s]) => (
                    <button key={k} data-testid={`admin-ticket-status-${k}`} onClick={() => onUpdate({ status: k })} className={`px-2.5 py-1.5 rounded-lg text-xs font-bold ${t.status === k ? s.c + " ring-2 ring-offset-1 ring-current" : "bg-slate-100 dark:bg-slate-800 text-slate-500"}`}>{s.l}</button>
                ))}
            </div>
        </div>
    );
}

export default function AdminSupportPage() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [status, setStatus] = useState("");
    const [mine, setMine] = useState(false);
    const [q, setQ] = useState("");
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null);
    const [agents, setAgents] = useState([]);

    const load = useCallback(() => {
        api.get("/support/tickets", { params: { status, q, assigned: mine ? "me" : "" } })
            .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); }).catch(() => {});
    }, [status, q, mine]);

    useEffect(() => { api.get("/support/agents").then((r) => setAgents(r.data.items || [])).catch(() => {}); }, []);
    useEffect(() => {
        const d = setTimeout(load, 250);
        const t = setInterval(() => { if (!document.hidden) load(); }, 8000);
        return () => { clearTimeout(d); clearInterval(t); };
    }, [load]);

    const onUpdate = async (patch) => {
        try {
            const r = await api.patch(`/support/tickets/${activeId}`, patch);
            setActive(r.data);
            load();
            toast.success("Güncellendi");
        } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
    };

    const onTicket = useCallback((t) => {
        setActive(t);
        setItems((prev) => prev.map((x) => x.id === t.id ? { ...x, unread: 0 } : x));
    }, []);

    return (
        <AdminLayout title="Destek Talepleri">
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden flex h-[calc(100vh-170px)] min-h-[520px]" data-testid="admin-support-page">
                <div className="w-[360px] flex-shrink-0 border-r border-slate-200 dark:border-slate-800 flex flex-col">
                    <div className="p-3 space-y-2 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800">
                            <Search className="h-4 w-4 text-slate-400" />
                            <input data-testid="admin-support-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Otel, başlık, rezervasyon…" className="w-full bg-transparent outline-none text-sm text-slate-900 dark:text-white" />
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {FILTERS.map((f) => (
                                <button key={f.v || "all"} data-testid={`admin-support-filter-${f.v || "all"}`} onClick={() => setStatus(f.v)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${status === f.v ? "bg-sky-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {f.l}{f.v && counts[f.v] != null ? ` (${counts[f.v]})` : ""}
                                </button>
                            ))}
                            <button data-testid="admin-support-filter-mine" onClick={() => setMine((m) => !m)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${mine ? "bg-violet-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>Bana atananlar</button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Talep bulunamadı.</div>}
                        {items.map((t) => <TicketRow key={t.id} t={t} active={t.id === activeId} onClick={() => { setActiveId(t.id); setActive(t); }} />)}
                    </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {activeId && active ? (
                        <>
                            <TicketHeader t={active} agents={agents} onUpdate={onUpdate} />
                            <div className="flex-1 min-h-0"><SupportChatThread ticketId={activeId} side="admin" onTicket={onTicket} /></div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2">
                            <MessageCircle className="h-10 w-10" />
                            <div className="text-sm">Soldan bir konuşma seçin</div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
